import type { ReactElement } from 'react';
import type { BriefTask, BriefTaskRow, ProposalBuildState } from '@shared/api';
import { ScrollText } from './ScrollText';
import './brief.css';

/**
 * "Preparing the brief" — docs/live-build.md §5 B. Three task cards, driven
 * entirely by `state.briefTasks` and `state.progress`. Never renders model
 * turns or tool activity — only resolved facts.
 */

const TASK_STATUS_LABEL: Record<BriefTask['status'], string> = {
  queued: 'Queued',
  working: 'Working',
  done: 'Done',
};

// Canonical shape (docs/live-build.md §3) for legacy/live states that predate
// Stream C's `deriveBriefTasks` — renders the same three cards, all skeleton.
const FALLBACK_TASKS: BriefTask[] = [
  {
    id: 'verify_business',
    title: 'Verify business',
    status: 'queued',
    rows: [
      { id: 'website', label: 'Website', value: null },
      { id: 'business_profile', label: 'Business profile', value: null },
      { id: 'category', label: 'Category', value: null },
    ],
  },
  {
    id: 'shape_campaign',
    title: 'Shape campaign',
    status: 'queued',
    rows: [
      { id: 'audience_size', label: 'Audience size', value: null },
      { id: 'geography', label: 'Geography', value: null },
      { id: 'imagery', label: 'Imagery', value: null },
    ],
  },
  {
    id: 'price_recommendation',
    title: 'Price recommendation',
    status: 'queued',
    rows: [
      { id: 'budget', label: 'Budget', value: null },
      { id: 'channel_mix', label: 'Channel mix', value: null },
      { id: 'flight_dates', label: 'Flight dates', value: null },
    ],
  },
];

function BriefRow({ row, last, settled }: { row: BriefTaskRow; last: boolean; settled: boolean }): ReactElement {
  const resolved = row.value !== null;
  return (
    <div className={`brief-row${last ? ' brief-row--last' : ''}`}>
      <ScrollText className="brief-row__label">{row.label}</ScrollText>
      <span className="brief-row__value-cell">
        {resolved ? (
          <ScrollText className="brief-row__value" key={row.value}>
            {row.value}
            <span className="brief-row__check" aria-hidden="true">
              ✓
            </span>
          </ScrollText>
        ) : settled ? (
          // The brief is ready without this row — nothing is still filling it.
          <ScrollText className="brief-row__value brief-row__value--unset">Not set</ScrollText>
        ) : (
          <span className="brief-row__pill" aria-hidden="true" />
        )}
      </span>
    </div>
  );
}

function BriefTaskCard({ task, index, settled }: { task: BriefTask; index: number; settled: boolean }): ReactElement {
  const resolvedRows = task.rows.filter((row) => row.value !== null).length;
  const totalRows = task.rows.length;
  const railWidth = settled ? 100 : totalRows > 0 ? (resolvedRows / totalRows) * 100 : 0;
  const status: BriefTask['status'] = settled ? 'done' : task.status;

  return (
    <article className="brief-card" aria-label={task.title}>
      <header className="brief-card__header">
        <span className="brief-card__index" aria-hidden="true">
          {String(index + 1).padStart(2, '0')}
        </span>
        <ScrollText as="h2" className="brief-card__title">{task.title}</ScrollText>
        <span className={`brief-card__status brief-card__status--${status}`}>
          <span className="brief-card__status-dot" aria-hidden="true" />
          {TASK_STATUS_LABEL[status]}
        </span>
      </header>
      <div className="brief-card__rail" aria-hidden="true">
        <span
          className={`brief-card__rail-fill${status === 'done' ? ' brief-card__rail-fill--done' : ''}`}
          style={{ width: `${railWidth}%` }}
        />
      </div>
      <div className="brief-card__rows">
        {task.rows.map((row, rowIndex) => (
          <BriefRow key={row.id} row={row} last={rowIndex === task.rows.length - 1} settled={settled} />
        ))}
      </div>
    </article>
  );
}

export function BriefPreparationView({ state }: { state: ProposalBuildState | null }): ReactElement {
  const tasks = state?.briefTasks.length ? state.briefTasks : FALLBACK_TASKS;
  // A brief ready to approve is finished: 100%, every card done.
  const settled = state?.phase === 'awaiting_confirmation';
  const progress = settled ? 100 : state?.progress ?? 0;
  const title = settled ? 'Brief ready' : 'Preparing the brief';

  return (
    <section className="brief-preparation-view" aria-label={title} aria-live="polite">
      <div className="brief-preparation-view__inner">
        <header className="brief-preparation-view__header">
          <h1 className="brief-preparation-view__title">{title}</h1>
          <span className="brief-preparation-view__percent">{progress}%</span>
        </header>
        <div className="brief-preparation-view__bar" aria-hidden="true">
          <span className="brief-preparation-view__bar-fill" style={{ width: `${progress}%` }} />
        </div>
        <div className="brief-preparation-view__cards">
          {tasks.map((task, index) => (
            <BriefTaskCard key={task.id} task={task} index={index} settled={settled} />
          ))}
        </div>
      </div>
    </section>
  );
}
